import { useEffect, useMemo, useRef, useState } from "react";
import CalendarGrid from "./CalendarGrid";
import { searchFilesByModule } from "../../services/FilesService/FilesService";
import toast from "react-hot-toast";
import Plyr from "plyr-react";
import "plyr-react/plyr.css";
import { useFilePreview } from "../../hooks/useFilePreview";
import FilePreviewModal from "../../components/FilePreviewModal/FilePreviewModal";
import { downloadFile, getFileExtension } from "../../utils/fileUtils";
import { extractYoutubeId } from "../../utils/youtube";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import {
  CalendarDays,
  Download,
  CheckCircle2,
  Copy,
  Eye,
  FileText,
  Video,
} from "lucide-react";

const groups = [
  { key: "Кіші топ", age: "2-3 жас" },
  { key: "Ортаңғы топ", age: "3-4 жас" },
  { key: "Ересек топ", age: "4-5 жас" },
  { key: "Мектепалды топ", age: "5-6 жас" },
];

const months = [
  "Қаңтар",
  "Ақпан",
  "Наурыз",
  "Сәуір",
  "Мамыр",
  "Маусым",
  "Шілде",
  "Тамыз",
  "Қыркүйек",
  "Қазан",
  "Қараша",
  "Желтоқсан",
];

const groupMap = {
  "Кіші топ": "kishi_top",
  "Ортаңғы топ": "ortangy_top",
  "Ересек топ": "eresek_top",
  "Мектепалды топ": "mektep_aldy_top",
};

const plyrOptions = {
  ratio: "16:9",
  youtube: { noCookie: true, modestbranding: 1, rel: 0 },
};

export default function ParentTool() {
  const [selectedGroup, setSelectedGroup] = useState("Мектепалды топ");
  const [selectedDate, setSelectedDate] = useState(
    () => new Date(new Date().getFullYear(), 8, 1)
  );
  // starts true — the first fetch begins right after mount, and initializing
  // to false would flash the "no materials" empty state before it
  const [loadingResults, setLoadingResults] = useState(true);
  const [video, setVideo] = useState(null); // { link, label }
  const [doc, setDoc] = useState(null); // file object with file_url
  const [previewTitle, setPreviewTitle] = useState("");

  const {
    isPreviewOpen,
    previewUrl,
    isLoading: isPreviewLoading,
    openPreview,
    closePreview,
  } = useFilePreview();

  // Stable handle for closing the preview from the fetch effect without
  // retriggering it (the hook returns fresh closures each render)
  const closePreviewRef = useRef(closePreview);
  closePreviewRef.current = closePreview;

  const selectedMonthName = months[selectedDate.getMonth()];

  // Fetch the month+group materials as soon as the selection changes
  useEffect(() => {
    let cancelled = false;
    // A preview left open would show the previous selection's document
    closePreviewRef.current();
    const fetchMaterials = async () => {
      setLoadingResults(true);
      try {
        const res = await searchFilesByModule({
          module_code: "ata_ana_bailanys",
          month: selectedDate.getMonth() + 1,
          group: groupMap[selectedGroup],
        });
        if (cancelled) return;

        const entries = res?.files || [];
        // Video may come back as a file entry with a link, or in the
        // separate links array
        const linkEntry = entries.find((f) => f.link);
        const apiLink = res?.links?.[0];
        setVideo(
          linkEntry
            ? { link: linkEntry.link, label: linkEntry.label }
            : apiLink
            ? { link: apiLink.url, label: apiLink.label }
            : null
        );
        // Prefer pure file entries so a row carrying both link and file
        // isn't presented as the video AND the document
        setDoc(
          entries.find((f) => f.file_url && !f.link) ||
            entries.find((f) => f.file_url) ||
            null
        );
      } catch (err) {
        if (cancelled) return;
        console.error("Fetch materials error:", err);
        setVideo(null);
        setDoc(null);
        toast.error("Материалдарды жүктеу кезінде қате орын алды.");
      } finally {
        if (!cancelled) setLoadingResults(false);
      }
    };

    fetchMaterials();
    return () => {
      cancelled = true;
    };
  }, [selectedDate, selectedGroup]);

  const handleCopyLink = async () => {
    if (!video?.link) return;
    try {
      await navigator.clipboard.writeText(video.link);
      toast.success("Сілтеме көшірілді!");
    } catch {
      // Clipboard API unavailable (http / old browser) — fallback
      const input = document.createElement("input");
      input.value = video.link;
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(input);
      if (copied) {
        toast.success("Сілтеме көшірілді!");
      } else {
        toast.error("Көшіру мүмкін болмады — сілтемені қолмен көшіріңіз.");
      }
    }
  };

  const videoId = extractYoutubeId(video?.link);

  // Plyr destroys and re-creates the player whenever source/options change
  // identity — memoize the source so unrelated re-renders don't restart
  // playback (options are a module-level constant)
  const plyrSource = useMemo(
    () =>
      videoId
        ? {
            type: "video",
            sources: [{ src: videoId, provider: "youtube" }],
          }
        : null,
    [videoId]
  );

  return (
    <div className="flex flex-col flex-1 items-center p-6 md:p-12 ">
      {/* Header */}
      <header className="w-full max-w-5xl mb-8">
        <div className="flex items-center justify-center gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#F3DA6D]">
              Ата-анамен байланыс құралы
            </h1>
            <p className="text-sm md:text-base text-gray-500">
              Айды таңдаңыз, топты белгілеңіз — видео, сілтеме және құжат пайда
              болады.
            </p>
          </div>
        </div>
      </header>

      {/* Selection */}
      <main className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-5xl">
        {/* Calendar Card */}
        <section className="bg-white rounded-2xl shadow-xl p-5 md:p-6 ">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#A16207]" />
              <h2 className="text-lg md:text-xl font-bold text-[#A16207]">
                Айлар
              </h2>
            </div>
          </div>
          <CalendarGrid
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />
        </section>

        {/* Group Card */}
        <section className="bg-white rounded-2xl shadow-xl p-5 md:p-6 flex flex-col ">
          <h2 className="font-bold text-lg md:text-xl mb-4 text-[#A16207]">
            Топтар
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {groups.map(({ key, age }) => {
              const active = selectedGroup === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedGroup(key)}
                  className="group relative text-left rounded-2xl p-4 border transition-all"
                  style={
                    active
                      ? {
                          borderColor: "#d6b94d",
                          backgroundColor: "#F3DA6D",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }
                      : { borderColor: "#e5e7eb" }
                  }
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-800">{key}</div>
                      <div
                        className="mt-1 inline-flex items-center text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: "#FEF9C3", color: "#A16207" }}
                      >
                        {age}
                      </div>
                    </div>
                    {active ? (
                      <CheckCircle2 className="w-5 h-5 text-[#A16207]" />
                    ) : (
                      <span className="w-5 h-5 rounded-full border border-slate-300" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <p className="mt-auto text-xs text-slate-600">
            Таңдалған ай: <strong>{selectedMonthName}</strong> · Топ:{" "}
            <strong>{selectedGroup}</strong>
          </p>
        </section>
      </main>

      {/* Results */}
      <section className="w-full max-w-5xl mt-6">
        {loadingResults ? (
          <div className="bg-white rounded-2xl shadow-xl p-10 flex justify-center">
            <SimpleLoader className="w-10 h-10 text-[#A16207]" />
          </div>
        ) : !video && !doc ? (
          <div className="bg-white rounded-2xl shadow-xl p-10 text-center text-slate-500">
            Бұл ай мен топ үшін материал әлі жүктелмеген.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Video Card */}
            {video && (
              <div className="bg-white rounded-2xl shadow-xl p-5 md:p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Video className="w-5 h-5 text-[#A16207]" />
                  <h2 className="text-lg font-bold text-[#A16207]">
                    {video.label || "Видео"}
                  </h2>
                </div>

                {videoId ? (
                  <div className="w-full aspect-video rounded-xl overflow-hidden mb-4">
                    <Plyr source={plyrSource} options={plyrOptions} />
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mb-4">
                    <a
                      href={video.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[#A16207] font-medium"
                    >
                      Видеоны жаңа қойындыда ашу
                    </a>
                  </p>
                )}

                {/* Copyable link */}
                <div className="mt-auto flex items-center gap-2">
                  <input
                    readOnly
                    value={video.link}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2 text-sm rounded-xl border border-gray-300 bg-gray-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#d6b94d]"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-black transition-colors"
                    style={{ backgroundColor: "#F3DA6D" }}
                  >
                    <Copy className="w-4 h-4" />
                    Көшіру
                  </button>
                </div>
              </div>
            )}

            {/* Document Card */}
            {doc && (
              <div className="bg-white rounded-2xl shadow-xl p-5 md:p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-[#A16207]" />
                  <h2 className="text-lg font-bold text-[#A16207]">
                    {doc.label || doc.file_name || "Құжат"}
                  </h2>
                </div>

                <p className="text-sm text-slate-500 break-all mb-1">
                  {doc.file_name}
                </p>
                {doc.file_size_mb ? (
                  <p className="text-xs text-slate-400 mb-4">
                    ({doc.file_size_mb.toFixed(2)} MB)
                  </p>
                ) : null}

                <div className="mt-auto flex gap-3">
                  <button
                    onClick={() => {
                      if (getFileExtension(doc.file_name) === "pdf") {
                        window.open(doc.file_url, "_blank", "noopener");
                      } else {
                        setPreviewTitle(doc.label || doc.file_name || "");
                        openPreview(doc.file_url, doc.file_name);
                      }
                    }}
                    disabled={isPreviewLoading}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl font-semibold border transition-colors disabled:opacity-60"
                    style={{ borderColor: "#d6b94d", color: "#A16207" }}
                  >
                    {isPreviewLoading ? (
                      <SimpleLoader className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                    Көру
                  </button>
                  <button
                    onClick={() => downloadFile(doc.file_url, doc.file_name)}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-black transition-colors"
                    style={{ backgroundColor: "#F3DA6D" }}
                  >
                    <Download className="w-4 h-4" />
                    Жүктеп алу
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Document preview modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={previewUrl}
        onClose={closePreview}
        title={previewTitle || "Алдын ала қарау"}
      />
    </div>
  );
}
